<template>
  <form @submit.prevent="signUp">
    <FormGroup>
      <View direction="column" gap="sm">
        <Text variant="h1">Welcome to Tallo</Text>
        <Text variant="body-large" color="secondary">Create an account to unlock the power of AI-driven leasing</Text>
      </View>

      <FormField label="Full Name" :error="form.fieldErrors('fullName')">
        <Input v-model="form.fields.fullName" placeholder="Type your full name" name="fullName" />
      </FormField>

      <FormField label="Email" :error="form.fieldErrors('email')">
        <Input v-model="form.fields.email" placeholder="Type your email" type="email" name="email" />
      </FormField>

      <View direction="column" gap="sm">
        <FormField class="password-form-field" label="Password" :error="form.fieldErrors('password')">
          <Input
            v-model="form.fields.password"
            placeholder="Create a password"
            type="password"
            name="password"
            @focus="showPasswordStrength = true"
          />
        </FormField>

        <PasswordStrength
          :show-component="showPasswordStrength"
          @password-status-change="setPasswordValidity"
          :password="form.fields.password"
        />
      </View>

      <Button type="submit" :loading="form.submitting">Create account</Button>

      <Text variant="body-small">
        <span>By proceeding, you agree to the&nbsp;</span>
        <Link class="terms-link" color="accent" :to="termsAndConditionsLink" target="_blank">Terms & Conditions</Link>
        <span>&nbsp;and&nbsp;</span>
        <Link class="terms-link" color="accent" :to="privacyAndPolicyLink" target="_blank">Privacy & Policy</Link>
      </Text>

      <FormSeparator />
      <InvestorGoogleSignInButton :flow="'sign-up'" />

      <Text variant="body-large" align="center">
        <span>Have an account?&nbsp;</span>
        <Link :to="signInRoute()" :color="'accent'">Log in</Link>
      </Text>
    </FormGroup>
  </form>
</template>

<script setup lang="ts">
import { email, required } from '@vuelidate/validators';
import { AxiosError } from 'axios';
import { ref } from 'vue';

import { useAuthStore } from '@tallo/auth';
import {
  Button,
  FormField,
  FormGroup,
  Input,
  Link,
  PasswordStrength,
  Text,
  View,
  FormSeparator,
  useAlertsStore,
  useForm,
} from '@tallo/design-system';
import { signInRoute, useNavigation } from '@tallo/investor/navigation';
import { termsAndConditionsLink, privacyAndPolicyLink } from '@tallo/navigation';

import InvestorGoogleSignInButton from './investor-google-sign-in-button.vue';

const authStore = useAuthStore();
const { openEmailConfirmation } = useNavigation();

const showPasswordStrength = ref(false);
const passwordValid = ref(false);
const setPasswordValidity = (valid: boolean) => {
  passwordValid.value = valid;
};

const alerts = useAlertsStore();

const form = useForm(
  {
    fullName: '',
    email: '',
    password: '',
  },
  {
    fullName: { required },
    email: { required, email },
    password: {
      required,
      strong: () => passwordValid.value,
    },
  },
);

async function signUp() {
  form.submit(async () => {
    try {
      const data = await authStore.investorSignUpWithCreds({
        name: form.fields.fullName,
        email: form.fields.email,
        password: form.fields.password,
      });

      openEmailConfirmation({ userId: data.userId, email: form.fields.email });
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 409) {
        alerts.error('Something went wrong. Please attempt to log in using this email.');
      } else {
        alerts.error('Something went wrong');
      }
    }
  });
}
</script>

<style lang="scss" scoped>
.terms-link {
  font-weight: var(--t-font-weight-medium);
}
</style>
